import React, { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Slider,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Cameraswitch as CameraswitchIcon,
  PhotoCamera as PhotoCameraIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as FocusIcon
} from '@mui/icons-material';

// Extended interfaces for zoom support
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
  zoom?: {
    min: number;
    max: number;
    step: number;
  };
}

interface ExtendedMediaTrackConstraintSet {
  zoom?: number;
}

interface CameraDialogProps {
  open: boolean;
  loading: boolean;
  error: string | null;
  cameraInitAttempts: number;
  availableCameras: MediaDeviceInfo[];
  currentCameraIndex: number;
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  onClose: () => void;
  onSwitchCamera: () => void;
  onCaptureImage: () => void;
}

// Update the ref interface to match what's expected
export interface CameraDialogRef {
  captureZoomedImage: () => string | null;
  getCurrentZoomLevel: () => number;
  getCurrentPanPosition: () => { x: number; y: number };
}

const CameraDialog = forwardRef<CameraDialogRef, CameraDialogProps>(({
  open,
  loading,
  error,
  cameraInitAttempts,
  availableCameras,
  currentCameraIndex,
  videoRef,
  canvasRef,
  onClose,
  onSwitchCamera,
  onCaptureImage
}, ref) => {
  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [maxZoom] = useState(3);
  const [minZoom] = useState(1);
  const [supportsNativeZoom, setSupportsNativeZoom] = useState(false);
  const [useCSSZoom, setUseCSSZoom] = useState(true);
  const [cropOverlay, setCropOverlay] = useState<{ width: number, height: number } | null>(null);
  // Touch/pinch zoom state
  const [initialDistance, setInitialDistance] = useState(0);
  const [initialZoom, setInitialZoom] = useState(1);
  const [isPinching, setIsPinching] = useState(false);
  
  // Pan state for zoomed view
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showInstructions, setShowInstructions] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  // Make sure captureZoomedImage is properly implemented
  // Update the captureZoomedImage function with better debugging
  const captureZoomedImage = useCallback((): string | null => {
    console.log('=== CAPTURE ZOOMED IMAGE START ===');
    console.log('videoRef.current:', !!videoRef.current);
    console.log('canvasRef.current:', !!canvasRef.current);
    console.log('zoomLevel:', zoomLevel);
    console.log('panX:', panX, 'panY:', panY);
    console.log('supportsNativeZoom:', supportsNativeZoom);
    console.log('useCSSZoom:', useCSSZoom);

    if (!videoRef.current || !canvasRef.current) {
      console.warn('Video or canvas ref not available');
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.warn('Canvas context not available');
      return null;
    }

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    console.log('Video dimensions:', { videoWidth, videoHeight });
    
    if (videoWidth === 0 || videoHeight === 0) {
      console.warn('Video dimensions are zero - camera may not be ready');
      return null;
    }
    
    // Set canvas size to video resolution
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    ctx.clearRect(0, 0, videoWidth, videoHeight);

    console.log('Canvas dimensions set to:', { width: canvas.width, height: canvas.height });

    if (zoomLevel > 1) {
      console.log('Processing zoomed capture');
      
      // For CSS zoom, we need to crop the video
      const cropWidth = videoWidth / zoomLevel;
      const cropHeight = videoHeight / zoomLevel;

      // ##  SHould check this maybe 
      // const cropHeight = videoHeight / zoomLevel;
// const cropWidth = cropHeight * (videoWidth / videoHeight); // maintain aspect ratio
      
      console.log('Crop dimensions:', { cropWidth, cropHeight });
      
      // Calculate center point
      const centerX = videoWidth / 2;
      const centerY = videoHeight / 2;
      
      // Convert pan values to video coordinate system
      let adjustedPanX = 0;
      let adjustedPanY = 0;
      
      const container = containerRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        console.log('Container dimensions:', { 
          width: containerRect.width, 
          height: containerRect.height 
        });
        
        // Calculate scale factors
        const scaleX = videoWidth / containerRect.width;
        const scaleY = videoHeight / containerRect.height;
        
        console.log('Scale factors:', { scaleX, scaleY });
        
        // Adjust pan values
        adjustedPanX = panX * scaleX;
        adjustedPanY = panY * scaleY;
        
        console.log('Adjusted pan:', { adjustedPanX, adjustedPanY });
      }
      // BEFORE 
      // Calculate crop position (center minus half crop size, adjusted for pan)
      const cropX = centerX - cropWidth / 2 - adjustedPanX;
      const cropY = centerY - cropHeight / 2 - adjustedPanY;
      
      // Clamp to video boundaries
      const clampedCropX = Math.max(0, Math.min(videoWidth - cropWidth, cropX));
      const clampedCropY = Math.max(0, Math.min(videoHeight - cropHeight, cropY));
      
      console.log('Final crop position:', { 
        cropX: clampedCropX, 
        cropY: clampedCropY,
        cropWidth,
        cropHeight
      });
      
      // Draw the cropped area scaled to full canvas
      ctx.drawImage(
        video,
        clampedCropX, clampedCropY, cropWidth, cropHeight, // Source rectangle (what to crop)
        0, 0, videoWidth, videoHeight // Destination rectangle (fill entire canvas)
      );
      
      console.log('Zoomed image drawn to canvas');
      
    } else {
      console.log('Processing normal (no zoom) capture');
      // No zoom - capture full video
      ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
    }

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    console.log('Image captured, data URL length:', dataUrl.length);
    console.log('=== CAPTURE ZOOMED IMAGE END ===');
    
    return dataUrl;
  }, [zoomLevel, panX, panY, supportsNativeZoom, useCSSZoom]);

  // Expose methods via useImperativeHandle
  useImperativeHandle(ref, () => ({
    captureZoomedImage,
    getCurrentZoomLevel: () => zoomLevel,
    getCurrentPanPosition: () => ({ x: panX, y: panY })
  }), [captureZoomedImage, zoomLevel, panX, panY]);

// /  NEW 
    useEffect(() => {
      if (containerRef.current) {
        const container = containerRef.current.getBoundingClientRect();
        const width = container.width / zoomLevel;
        const height = container.height / zoomLevel;
        setCropOverlay({ width, height });
      }
    }, [zoomLevel, containerRef.current]);


  // Check zoom capabilities when video stream is available
  useEffect(() => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const videoTrack = stream.getVideoTracks()[0];
      
      if (videoTrack) {
        try {
          const capabilities = videoTrack.getCapabilities() as ExtendedMediaTrackCapabilities;
          if (capabilities.zoom) {
            setSupportsNativeZoom(true);
            console.log('Native camera zoom supported');
          } else {
            // Try to detect zoom support by attempting to apply constraints
            videoTrack.applyConstraints({
              advanced: [{ zoom: 1 } as ExtendedMediaTrackConstraintSet]
            } as MediaTrackConstraints).then(() => {
              setSupportsNativeZoom(true);
              console.log('Native camera zoom detected via constraints');
            }).catch(() => {
              console.log('Native camera zoom not supported, using CSS zoom');
              setSupportsNativeZoom(false);
            });
          }
        } catch (error) {
          console.log('Could not check zoom capabilities, using CSS zoom:', error);
          setSupportsNativeZoom(false);
        }
      }
    }
  }, [videoRef.current?.srcObject]);

  // Apply native zoom to video track
  const applyNativeZoom = useCallback(async (zoom: number) => {
    if (!videoRef.current?.srcObject || !supportsNativeZoom) return false;
    
    const stream = videoRef.current.srcObject as MediaStream;
    const videoTrack = stream.getVideoTracks()[0];
    
    try {
      await videoTrack.applyConstraints({
        advanced: [{ zoom: zoom } as ExtendedMediaTrackConstraintSet]
      } as MediaTrackConstraints);
      return true;
    } catch (error) {
      console.warn('Native zoom failed:', error);
      return false;
    }
  }, [supportsNativeZoom]);

  // Handle zoom change
  const handleZoomChange = useCallback(async (newZoom: number) => {
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
    setZoomLevel(clampedZoom);
    
    // Try native zoom first, fallback to CSS zoom
    const nativeZoomSuccess = await applyNativeZoom(clampedZoom);
    if (!nativeZoomSuccess && useCSSZoom) {
      // CSS zoom will be applied via the transform style
      console.log('Using CSS zoom:', clampedZoom);
    }
  }, [minZoom, maxZoom, applyNativeZoom, useCSSZoom]);

  // Reset pan when zoom changes
  useEffect(() => {
    if (zoomLevel === 1) {
      setPanX(0);
      setPanY(0);
    }
  }, [zoomLevel]);

  // Calculate distance between two touches
  const getDistance = (touch1: React.Touch, touch2: React.Touch) => {
    return Math.hypot(
      touch1.clientX - touch2.clientX,
      touch1.clientY - touch2.clientY
    );
  };

  // Touch event handlers for pinch-to-zoom
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getDistance(e.touches[0], e.touches[1]);
      setInitialDistance(distance);
      setInitialZoom(zoomLevel);
      setIsPinching(true);
      setIsDragging(false);
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Single touch for panning when zoomed
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - panX,
        y: e.touches[0].clientY - panY
      });
    }
  }, [zoomLevel, panX, panY]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && isPinching && initialDistance > 0) {
      e.preventDefault();
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / initialDistance;
      const newZoom = initialZoom * scale;
      
      handleZoomChange(newZoom);
    } else if (e.touches.length === 1 && isDragging && zoomLevel > 1) {
      e.preventDefault();
      const newPanX = e.touches[0].clientX - dragStart.x;
      const newPanY = e.touches[0].clientY - dragStart.y;
      
      // Limit panning based on zoom level
      const maxPan = (zoomLevel - 1) * 100;
      setPanX(Math.max(-maxPan, Math.min(maxPan, newPanX)));
      setPanY(Math.max(-maxPan, Math.min(maxPan, newPanY)));
    }
  }, [isPinching, initialDistance, initialZoom, handleZoomChange, isDragging, dragStart, zoomLevel]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
      setInitialDistance(0);
    }
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  }, []);

  // Mouse event handlers for desktop panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - panX,
        y: e.clientY - panY
      });
    }
  }, [zoomLevel, panX, panY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;
      
      const maxPan = (zoomLevel - 1) * 100;
      setPanX(Math.max(-maxPan, Math.min(maxPan, newPanX)));
      setPanY(Math.max(-maxPan, Math.min(maxPan, newPanY)));
    }
  }, [isDragging, dragStart, zoomLevel]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Zoom button handlers
  const handleZoomIn = () => {
    handleZoomChange(zoomLevel + 0.2);
  };

  const handleZoomOut = () => {
    handleZoomChange(zoomLevel - 0.2);
  };

  const resetZoom = () => {
    handleZoomChange(1);
    setPanX(0);
    setPanY(0);
  };


  
  // Handle capture with zoom awareness
  const handleCapture = useCallback(() => {
    console.log('Capturing image with zoom level:', zoomLevel);
    onCaptureImage();
  }, [onCaptureImage, zoomLevel]);

  // Detect if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Calculate video transform
  const videoTransform = useCSSZoom ? 
    `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px) scaleX(-1)` : 
    `translate(${panX}px, ${panY}px) scaleX(-1)`;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile} // Add this line - fullscreen on mobile only
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2, // Remove border radius on mobile for true fullscreen
          overflow: 'hidden',
          backgroundColor: '#000',
        }
      }}
    >
      {/* #### Testing here */}
      {/* <DialogContent sx={{ p: 0, position: 'relative', height: isMobile ? '100vh' : { xs: 400, sm: 500 } }}></DialogContent> */}
      <DialogContent
  sx={{
    p: 0,
    position: 'relative',
    height: isMobile ? '100vh' : 'calc(100vh - 100px)', // Use most of viewport height on desktop
    maxHeight: '100vh',
    overflow: 'hidden'
  }}
>
        {loading && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}>
            <CircularProgress color="primary" />
            <Typography color="white" sx={{ mt: 2 }}>
              Initializing camera...
            </Typography>
            {cameraInitAttempts > 0 && (
              <Typography color="white" variant="caption" sx={{ mt: 1, maxWidth: '80%', textAlign: 'center' }}>
                {cameraInitAttempts === 1 
                  ? "First attempt failed. Trying alternate camera..." 
                  : "Still having trouble. Try the Switch Camera button."}
              </Typography>
            )}
          </Box>
        )}
        
        {error && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            p: 3,
          }}>
            <Alert severity="error" sx={{ width: '100%', maxWidth: 500, mb: 2 }}>
              {error}
            </Alert>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={onClose}
                sx={{ color: 'white', borderColor: 'white' }}
              >
                Close
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={onSwitchCamera}
              >
                Try Switch Camera
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Camera container with touch support */}
        <Box
          ref={containerRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            touchAction: 'none',
            overflow: 'hidden',
            cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          ///Drag functionality
          // onMouseDown={handleMouseDown}
          // onMouseMove={handleMouseMove}
          // onMouseUp={handleMouseUp}
          // onMouseLeave={handleMouseUp}
        >
          <video 
            ref={videoRef}
            autoPlay 
            playsInline
            muted
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',
              backgroundColor: '#000',
                transform: `${videoTransform} scaleX(-1)`, // <-- added flip
              transformOrigin: 'center center',
              transition: isDragging ? 'none' : 'transform 0.1s ease-out'
            }}
          />
          {cropOverlay && (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: `${cropOverlay.width}px`,
      height: `${cropOverlay.height}px`,
      transform: 'translate(-50%, -50%)',
      border: '2px dashed #00FF00',
      boxShadow: '0 0 10px rgba(0,255,0,0.4)',
      pointerEvents: 'none',
      zIndex: 5
    }}
  />
)}
        </Box>
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Camera info overlay */}
        {!loading && !error && availableCameras.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: 1,
              px: 1,
              py: 0.5,
            }}
          >
            <Typography variant="caption" color="white">
              Camera {currentCameraIndex + 1}/{availableCameras.length}
            </Typography>
          </Box>
        )}

        {/* Zoom controls overlay */}
        {!loading && !error && (
          <>
            {/* Zoom level indicator */}
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                backgroundColor: 'rgba(0,0,0,0.7)',
                borderRadius: 1,
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography variant="caption" color="white">
                Zoom: {zoomLevel.toFixed(1)}x {!supportsNativeZoom && '(CSS)'}
              </Typography>
            </Box>

            {/* Desktop zoom controls */}
            {!isMobile && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 10,
                  bottom: 60,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  borderRadius: 1,
                  p: 1
                }}
              >
                <Tooltip title="Zoom In">
                  <IconButton 
                    onClick={handleZoomIn} 
                    size="small" 
                    sx={{ color: 'white' }}
                    disabled={zoomLevel >= maxZoom}
                  >
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Reset Zoom">
                  <IconButton 
                    onClick={resetZoom} 
                    size="small" 
                    sx={{ color: 'white' }}
                  >
                    <FocusIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Zoom Out">
                  <IconButton 
                    onClick={handleZoomOut} 
                    size="small" 
                    sx={{ color: 'white' }}
                    disabled={zoomLevel <= minZoom}
                  >
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Zoom slider for desktop */}
            {!isMobile && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 200,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  borderRadius: 1,
                  px: 2,
                  py: 1
                }}
              >
                <Slider
                  value={zoomLevel}
                  min={minZoom}
                  max={maxZoom}
                  step={0.1}
                  onChange={(_, value) => handleZoomChange(value as number)}
                  sx={{
                    color: 'white',
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'white',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: 'white',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Mobile zoom instructions */}
          {!loading && !error && isMobile && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 60,
                left: 10,
                right: 10,
                backgroundColor: 'rgba(0,0,0,0.7)',
                borderRadius: 1,
                px: 2,
                py: 1,
                textAlign: 'center',
                '@keyframes fadeOut': {
                  from: { opacity: 1 },
                  to: { opacity: 0 }
                },
                animation: 'fadeOut 1s 1s forwards'
              }}
            >
              <Typography variant="caption" color="white">
                💡 Pinch to zoom • Drag to pan when zoomed
              </Typography>
            </Box>
          )}

        {/* Enhanced mobile zoom instructions with better visibility */}
        {!loading && !error && isMobile && zoomLevel === 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 60,
              left: 10,
              right: 10,
              backgroundColor: 'rgba(0,0,0,0.8)',
              borderRadius: 1,
              px: 2,
              py: 1,
              textAlign: 'center',
              '@keyframes fadeOut': {
                from: { opacity: 1 },
                to: { opacity: 0 }
              },
              animation: 'fadeOut 1s 3s forwards' // Show longer on mobile
            }}
          >
            <Typography variant="caption" color="white" sx={{ fontWeight: 'medium' }}>
              💡 Pinch to zoom • Drag to pan when zoomed • Zoom is preserved in capture
            </Typography>
          </Box>
        )}

        {/* Mobile zoom level indicator when zoomed */}
        {!loading && !error && isMobile && zoomLevel > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 60,
              left: 10,
              backgroundColor: 'rgba(0,150,0,0.9)',
              borderRadius: 1,
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="caption" color="white" sx={{ fontWeight: 'bold' }}>
              🔍 {zoomLevel.toFixed(1)}x Zoom Active
            </Typography>
          </Box>
        )}

        {/* Desktop instructions */}
        {!loading && !error && !isMobile && zoomLevel > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 50,
              left: 10,
              backgroundColor: 'rgba(0,100,0,0.7)',
              borderRadius: 1,
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="caption" color="white">
              🖱️ Click and drag to pan
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        justifyContent: 'space-between', 
        bgcolor: '#000',
        p: 2
      }}>
        <Button 
          onClick={onClose} 
          variant="text"
          startIcon={<CloseIcon />}
          sx={{ color: '#fff' }}
        >
          Cancel
        </Button>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isMobile && (
            <Tooltip title="Reset Zoom & Pan">
              <Button
                onClick={resetZoom}
                color="info"
                variant="outlined"
                startIcon={<FocusIcon />}
                sx={{ color: '#fff', borderColor: '#fff' }}
                disabled={loading}
                size="small"
              >
                Reset
              </Button>
            </Tooltip>
          )}
          
          <Button
            onClick={onSwitchCamera}
            color="info"
            variant="contained"
            startIcon={<CameraswitchIcon />}
            sx={{ color: '#fff' }}
            disabled={loading && cameraInitAttempts === 0}
          >
            Switch
          </Button>
          
          <Button 
            onClick={handleCapture}
            variant="contained"
            startIcon={<PhotoCameraIcon />}
            color="primary"
            disabled={loading || !!error}
            sx={{
              // Make capture button more prominent when zoomed
              ...(zoomLevel > 1 && {
                bgcolor: 'success.main',
                '&:hover': {
                  bgcolor: 'success.dark'
                }
              })
            }}
          >
            Capture {zoomLevel > 1 && `(${zoomLevel.toFixed(1)}x)`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
});

CameraDialog.displayName = 'CameraDialog';
export default CameraDialog;